import * as z from "zod";
import {
  type DisputeAlertStatus,
  type DisputeAlertType,
} from "./dispute-alert-definition";
import { type DisputeStatus } from "./dispute-definition";

/**
 * Repo row shapes for the merchant chargeback-analytics sections. Amounts are
 * as each table stores them: `dispute.amount` major units, OTD minor units.
 */

/**
 * One row per (BIN prefix, bank name, currency). `bankName` doubles as the
 * payment-method label for non-card disputes, so the handler buckets both.
 */
export interface PaymentMethodBreakdownRow {
  binPrefix?: string | null;
  bankName?: string | null;
  source: string;
  currency: string;
  count: number;
  totalAmount: number;
}

/** A chargeback's order and dates, for the by-product and time-to-event joins. */
export interface DisputeOrderRow {
  sourceOrderId: string;
  status: DisputeStatus;
  amount: number;
  currency: string;
  initiatedAt: Date;
}

/** One in-range chargeback, with the identity the miss matcher works from. */
export interface MissedChargebackCandidateRow {
  id: string;
  source: string;
  sourceOrderId?: string | null;
  status: DisputeStatus;
  reason?: string | null;
  networkReasonCode?: string | null;
  amount: number;
  currency: string;
  initiatedAt: Date;
  bin?: string | null;
  bankName?: string | null;
}

/** An alert's transaction identity, for the read-time chargeback matcher. */
export interface MissMatchingAlertRow {
  id: string;
  type: DisputeAlertType;
  status: DisputeAlertStatus;
  authCode?: string | null;
  cardLastFour?: string | null;
  referenceTransactionDate?: Date | null;
  referenceTransactionAmountMinor?: number | null;
  referenceTransactionCurrency?: string | null;
  /** Hex of the Mongo order id automation refunded against, when it did. */
  refundedOrderIdHex?: string | null;
  linkedDisputeId?: string | null;
  respondBy?: Date | null;
  respondedAt?: Date | null;
  decisionRule?: string | null;
  declineReason?: string | null;
  deployedRefundAmountMinor?: number | null;
  createdAt: Date;
}

/** A settled leg of a disputed order, keyed for the read-time miss matcher. */
export interface MissSettledTransactionRow {
  orderIdHex: string;
  authCode?: string | null;
  cardLastFour?: string | null;
  amountMinor: number;
  currency: string;
  processedAt?: Date | null;
  sourceCreatedAt: Date;
}

/** Settled transaction volume per platform gateway, for the per-rail ratios. */
export interface GatewayCountRow {
  gateway?: string | null;
  settledCount: number;
}

/** Raw network reason codes per alert program; mapped to reasons in the handler. */
export interface AlertReasonCodeRow {
  type: DisputeAlertType;
  reasonCode?: string | null;
  providerAlertType?: string | null;
  count: number;
}

/** Alerts grouped by the BIN prefix their card falls under, and by network. */
export interface AlertBrandCountRow {
  binPrefix?: string | null;
  type: DisputeAlertType;
  count: number;
}

/** Whole days from the disputed transaction to the alert; may be negative. */
export interface AlertTimeToEventRow {
  days: number;
  count: number;
}

/**
 * Monthly transaction volume per card brand. `totalCount` sits beside the
 * settled denominator so a caller can see how much the settled filter dropped.
 */
export interface MonthlyTransactionCountRow {
  month: Date;
  cardBrand?: string | null;
  settledCount: number;
  totalCount: number;
}

/** One slice of the prevention composition; chargebacks are the missed slice. */
export const coverageSliceSchema = z.object({
  key: z.string(),
  count: z.coerce.number(),
  share: z.coerce.number().nullable(),
});

/** Paired alert/dispute counts for one age bucket of the time-to-dispute chart. */
export const timeToEventBucketSchema = z.object({
  bucket: z.string(),
  label: z.string(),
  alertCount: z.coerce.number(),
  disputeCount: z.coerce.number(),
});

/** Per-month rates; nulls where transaction volume is unavailable. */
export const monthlyDisputeRateSchema = z.object({
  month: z.string(),
  chargebackCount: z.coerce.number(),
  alertCount: z.coerce.number(),
  transactionCount: z.coerce.number(),
  ratio: z.coerce.number().nullable(),
  counterfactualRatio: z.coerce.number().nullable(),
});

export const brandDisputeRateSchema = z.object({
  cardBrand: z.string().nullable(),
  label: z.string(),
  chargebackCount: z.coerce.number(),
  transactionCount: z.coerce.number(),
  ratio: z.coerce.number().nullable(),
});

/** Per-payment-rail rates; volume is null above the gateway-scan guard. */
export const railDisputeRateSchema = z.object({
  rail: z.enum(["card", "paypal", "bnpl", "other"]),
  label: z.string(),
  chargebackCount: z.coerce.number(),
  transactionCount: z.coerce.number().nullable(),
  ratio: z.coerce.number().nullable(),
  denominatorSource: z.enum(["gateway", "card_brand"]).nullable(),
});

/** What happened to the chargebacks that got through, within the range. */
export const missedOutcomeSchema = z.object({
  wonCount: z.coerce.number(),
  lostCount: z.coerce.number(),
  acceptedCount: z.coerce.number(),
  inFlightCount: z.coerce.number(),
  /** Chargebacks in a status outside the four buckets, so the split is exact. */
  otherCount: z.coerce.number(),
});

/**
 * Coverage + ratios: one payload, since both read the same alert, chargeback
 * and transaction-volume aggregates.
 */
export const coverageStatsSchema = z.object({
  rdrCount: z.coerce.number(),
  cdrnCount: z.coerce.number(),
  ethocaCount: z.coerce.number(),
  chargebackCount: z.coerce.number(),
  slices: z.array(coverageSliceSchema),
  preventedCount: z.coerce.number(),
  preventedShare: z.coerce.number().nullable(),
  catchRate: z.coerce.number().nullable(),
  /** Settled card volume; the denominator behind every rate here. */
  cardTransactionCount: z.coerce.number(),
  /** Live rows before the settled filter, so a shrunken denominator is visible. */
  totalTransactionCount: z.coerce.number(),
  transactionDataAvailable: z.boolean(),
  withAlertsRatio: z.coerce.number().nullable(),
  withoutAlertsRatio: z.coerce.number().nullable(),
  thresholdRatio: z.coerce.number(),
  timeToEvent: z.array(timeToEventBucketSchema),
  timeToEventUnknownAlertCount: z.coerce.number(),
  timeToEventUnknownDisputeCount: z.coerce.number(),
  monthly: z.array(monthlyDisputeRateSchema),
  byBrand: z.array(brandDisputeRateSchema),
  byRail: z.array(railDisputeRateSchema),
  /** Prevented alerts that still cost a newly deployed refund. */
  refundsDeployedCount: z.coerce.number(),
  refundsDeployedAmountUsd: z.coerce.number(),
  /** Alerts the matcher could not tie to exactly one order. */
  orderUnmatchedCount: z.coerce.number(),
  /** Share of alerts matched to an order; null when no alerts landed. */
  orderMatchRate: z.coerce.number().nullable(),
  missedOutcome: missedOutcomeSchema,
  alertsIncluded: z.boolean(),
  truncated: z.boolean(),
  errors: z.array(z.string()).nullish(),
});

export type CoverageStats = z.infer<typeof coverageStatsSchema>;

export const paymentMethodStatSchema = z.object({
  key: z.string(),
  label: z.string(),
  kind: z.enum(["card", "payment_method", "unknown"]),
  cardBrand: z.string().nullable(),
  /** False for wallets and BNPL, which no card-network alert can prevent. */
  protectable: z.boolean(),
  count: z.coerce.number(),
  totalAmount: z.coerce.number(),
});

export const paymentMethodStatsSchema = z.object({
  methods: z.array(paymentMethodStatSchema),
  totalCount: z.coerce.number(),
  currency: z.string(),
  errors: z.array(z.string()).nullish(),
});

export type PaymentMethodStats = z.infer<typeof paymentMethodStatsSchema>;

export const unifiedReasonStatSchema = z.object({
  reason: z.string(),
  label: z.string(),
  disputeCount: z.coerce.number(),
  alertCount: z.coerce.number(),
});

export const unifiedReasonStatsSchema = z.object({
  reasons: z.array(unifiedReasonStatSchema),
  /** Alerts whose network code we do not model; shown, never redistributed. */
  unclassifiedAlertCount: z.coerce.number(),
  alertsIncluded: z.boolean(),
  errors: z.array(z.string()).nullish(),
});

export type UnifiedReasonStats = z.infer<typeof unifiedReasonStatsSchema>;

export const disputeProductStatSchema = z.object({
  productKey: z.string(),
  title: z.string(),
  count: z.coerce.number(),
  totalAmount: z.coerce.number(),
});

export const disputeProductStatsSchema = z.object({
  products: z.array(disputeProductStatSchema),
  matchedCount: z.coerce.number(),
  /** Chargebacks whose order is not in Redo; reported so shares stay honest. */
  unmatchedCount: z.coerce.number(),
  totalCount: z.coerce.number(),
  currency: z.string(),
  truncated: z.boolean(),
  errors: z.array(z.string()).nullish(),
});

export type DisputeProductStats = z.infer<typeof disputeProductStatsSchema>;
